'use client';

import { faClose } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { Button } from '@/components/atoms';
import { ScoreFormatConfig } from '@/components/constants/types';
import { MatchScoreWrapper } from '@/components/molecules/ScoreCards/MatchScoreWrapper';
import { AlertScoreModalContent } from '@/components/molecules/ScoreCards/modals/AlertScoreModalContent';
import { CancelMatchModalContent } from '@/components/molecules/ScoreCards/modals/CancelMatchModalContent';
import { ForfeitAndRetireModalContent } from '@/components/molecules/ScoreCards/modals/ForfeitAndRetireModalContent';
import { PlayerNames } from '@/components/molecules/ScoreCards/ScoreCardWrapper';
import { ModalWrapper } from '@/components/organisms';
import { getMatchupStatusApiValue, MatchupStatusEnum } from '@/domain/constants/constants';
import { SubMatch } from '@/entities';
import { GameScore } from '@/entities/score/GameScore';
import { RetireOrForfeitGameInfo } from '@/entities/score/RetireOrForfeitGameInfo';
import { matchKeys } from '@/hooks/matches/matchKeys';
import { useUpdateMatchMutation } from '@/hooks/matches/useUpdateMatch';
import { useUpdateMatchup } from '@/hooks/matches/useUpdateMatchup';
import { MatchScores } from '@/lib/types';
import { createMatchScoreUpdateData, isScoreIrregular } from '@/utils/helpers';
import { calculateScoreAfterRetireOrForfeit, determineInitialState, mapStateToMatchScores } from '@/utils/score-utils';

interface Props {
	matchupUuid: string;
	match: SubMatch;
	awayTeamTitle: string;
	homeTeamTitle: string;
	awayTeamUuid: string;
	homeTeamUuid: string;
	matchupStatus: MatchupStatusEnum;
	isOpen: boolean;
	onOpenChange: (open: boolean) => void;
	playerNames: PlayerNames;
}

export interface MatchScoreState {
	games: { away: GameScore; home: GameScore }[];
	isModalOpen: boolean;
	retireOrForfeitGameInfo: RetireOrForfeitGameInfo | null;
	isCancelModalOpen: boolean;
}

export const MatchScorecardModal = ({
	matchupUuid,
	match,
	awayTeamTitle,
	homeTeamTitle,
	awayTeamUuid,
	homeTeamUuid,
	matchupStatus,
	isOpen,
	onOpenChange,
	playerNames
}: Props) => {
	const [state, setState] = useState(determineInitialState(match));
	const [isAlertScoreModalOpen, setIsAlertScoreModalOpen] = useState<boolean>(false);
	const [textWarningArray, setTextWarningArray] = useState<string[]>([]);

	const queryClient = useQueryClient();
	const { mutate: updateMatchMutation, isPending: isUpdatingMatch } = useUpdateMatchMutation({
		onSuccessCallback: () => {
			toast.success('Match updated successfully');
			queryClient.invalidateQueries({ queryKey: matchKeys.matches(matchupUuid) });
			onOpenChange(false);
		}
	});
	const { mutate: updateMatchupMutation } = useUpdateMatchup();

	const updateScore = (gameNumber: number, team: 'away' | 'home', newValue: GameScore) => {
		const fieldIndex = gameNumber - 1;
		setState((oldState) => ({
			...oldState,
			games: oldState.games.map((game, i) =>
				i === fieldIndex
					? {
							away: team === 'away' ? newValue : game.away,
							home: team === 'home' ? newValue : game.home
						}
					: game
			)
		}));
	};

	useEffect(() => {
		setState(determineInitialState(match));
	}, [match]);

	const scoreFormatConfig: ScoreFormatConfig = {
		maxGames: match.scoreFormatMaxGames ?? 0,
		gameBest: match.scoreFormatGameBest ?? 0,
		gameBestOutOf: match.scoreFormatGameBestOutOf ?? 0,
		games: [
			{ max: match.scoreFormatGame1Max ?? 0, winBy: match.scoreFormatGame1WinBy ?? 0 },
			{ max: match.scoreFormatGame2Max ?? 0, winBy: match.scoreFormatGame2WinBy ?? 0 },
			{ max: match.scoreFormatGame3Max ?? 0, winBy: match.scoreFormatGame3WinBy ?? 0 },
			{ max: match.scoreFormatGame4Max ?? 0, winBy: match.scoreFormatGame4WinBy ?? 0 },
			{ max: match.scoreFormatGame5Max ?? 0, winBy: match.scoreFormatGame5WinBy ?? 0 }
		]
	};

	const saveScore = () => {
		const currentScores: MatchScores = mapStateToMatchScores(state);
		const submittedScores = createMatchScoreUpdateData(currentScores, homeTeamUuid, awayTeamUuid, scoreFormatConfig);
		const textWarningArray = isScoreIrregular(scoreFormatConfig, submittedScores);
		setTextWarningArray(textWarningArray);

		if (textWarningArray.length > 0) {
			setIsAlertScoreModalOpen(true);
		} else {
			handleScoreSubmit();
		}
	};

	const handleScoreSubmit = () => {
		setIsAlertScoreModalOpen(false);
		const currentScores: MatchScores = mapStateToMatchScores(state);
		const submittedScores = createMatchScoreUpdateData(currentScores, homeTeamUuid, awayTeamUuid, scoreFormatConfig);
		updateMatchMutation(
			{
				matchId: match.matchUuid,
				updateData: submittedScores
			},
			{
				onSuccess: () => {
					if (matchupStatus !== MatchupStatusEnum.IN_PROGRESS_MATCHUP_STATUS) {
						updateMatchupMutation({
							matchup_uuid: matchupUuid,
							matchup_status: getMatchupStatusApiValue(MatchupStatusEnum.IN_PROGRESS_MATCHUP_STATUS)
						});
					}
				}
			}
		);
	};

	const onRetireOrForfeitSelect = (side: 'away' | 'home', type: 'forfeit' | 'retire' | 'undo', gameNumber: number) => {
		setState((oldState) => ({
			...oldState,
			retireOrForfeitGameInfo: {
				type: type,
				side: side,
				gameNumber: gameNumber,
				teamName: side === 'away' ? awayTeamTitle : homeTeamTitle
			}
		}));
	};

	const onRetireOrForfeitConfirm = () => {
		if (!state.retireOrForfeitGameInfo) return;

		const fieldIndex = state.retireOrForfeitGameInfo.gameNumber - 1;
		if (!state.games[fieldIndex]) {
			return;
		}
		const initialGame = determineInitialState(match).games[fieldIndex];
		const wasGameRetiredOrForfeited =
			state.retireOrForfeitGameInfo.side === 'away'
				? initialGame?.away.isForfeited || initialGame?.away.isRetired
				: initialGame?.home.isForfeited || initialGame?.home.isRetired;
		const newScore = calculateScoreAfterRetireOrForfeit(
			state.games[fieldIndex].away.score,
			state.games[fieldIndex].home.score,
			state.retireOrForfeitGameInfo,
			match
		);

		setState((oldState) => ({
			...oldState,
			games: oldState.games.map((game, i) =>
				i === fieldIndex
					? {
							away:
								state.retireOrForfeitGameInfo?.side === 'away'
									? {
											isRetired: state.retireOrForfeitGameInfo.type === 'retire',
											isForfeited: state.retireOrForfeitGameInfo.type === 'forfeit',
											isUndo: Boolean(state.retireOrForfeitGameInfo.type === 'undo' && wasGameRetiredOrForfeited),
											score: newScore.away
										}
									: {
											isRetired: state.retireOrForfeitGameInfo?.type !== 'undo' ? false : game.away.isRetired,
											isForfeited: state.retireOrForfeitGameInfo?.type !== 'undo' ? false : game.away.isForfeited,
											isUndo: false,
											score: newScore.away
										},
							home:
								state.retireOrForfeitGameInfo?.side === 'home'
									? {
											isRetired: state.retireOrForfeitGameInfo.type === 'retire',
											isForfeited: state.retireOrForfeitGameInfo.type === 'forfeit',
											isUndo: Boolean(state.retireOrForfeitGameInfo.type === 'undo' && wasGameRetiredOrForfeited),
											score: newScore.home
										}
									: {
											isRetired: state.retireOrForfeitGameInfo?.type !== 'undo' ? false : game.home.isRetired,
											isForfeited: state.retireOrForfeitGameInfo?.type !== 'undo' ? false : game.home.isForfeited,
											isUndo: false,
											score: newScore.home
										}
						}
					: game
			),
			retireOrForfeitGameInfo: null
		}));
	};

	if (!isOpen) return null;

	const isMatchInitiallyRetired = state.retireOrForfeitGameInfo
		? state.games[state.retireOrForfeitGameInfo.gameNumber - 1]?.[state.retireOrForfeitGameInfo?.side === 'away' ? 'away' : 'home']?.isRetired
		: false;

	const onClose = () => {
		onOpenChange(false);
		setState(determineInitialState(match));
	};

	return (
		<ModalWrapper
			title={match.roundText || `${match.playerGroupTitle} ${match.formatTitle}`}
			onClose={onClose}
			closeOnOutsideClick
			showModalHeader={false}
			className="max-w-[430px] py-4"
		>
			{!state.retireOrForfeitGameInfo && !isAlertScoreModalOpen && !state.isCancelModalOpen && (
				<div className="flex flex-col gap-3">
					{/* Modal Header*/}
					<div className="relative w-full">
						<p className="text-center font-medium text-gray-900 text-lg">
							{match.roundText || `${match.playerGroupTitle} ${match.formatTitle}`}
						</p>
						<FontAwesomeIcon
							icon={faClose}
							className="cursor-pointer text-xl font-normal absolute right-0 top-1/2 -translate-y-1/2"
							onClick={onClose}
						/>
					</div>
					<hr />
					<p className="font-medium text-base text-gray-900 text-center">{match.scoreFormatShorthand}</p>
					<div className="flex flex-col items-center">
						<p className="text-gray-900 font-bold text-lg">{`${awayTeamTitle} (A)`}</p>
						<p className="text-gray-900 font-medium text-lg">{playerNames.teamTwoPlayerOneName}</p>
						{match.teamOnePlayerTwoName ? <p className="text-gray-900 font-medium text-lg">{playerNames.teamTwoPlayerTwoName}</p> : null}
					</div>

					<MatchScoreWrapper
						state={state}
						updateScore={updateScore}
						onRetireOrForfeitSelect={onRetireOrForfeitSelect}
						matchupStatus={matchupStatus}
						awayTeamFinalScore={match.teamTwoFinalScore}
						homeTeamFinalScore={match.teamOneFinalScore}
						winner={match.winner}
						isLocked={false}
						gameStatuses={[
							match.gameOneStatus!,
							match.gameTwoStatus!,
							match.gameThreeStatus!,
							match.gameFourStatus!,
							match.gameFiveStatus!
						]}
					/>

					<div className="flex flex-col items-center">
						<p className="text-gray-900 font-bold text-lg">{`${homeTeamTitle} (H)`}</p>
						<p className="text-gray-900 font-medium text-lg">{playerNames.teamOnePlayerOneName}</p>
						{match.teamTwoPlayerTwoName ? <p className="text-gray-900 font-medium text-lg">{playerNames.teamOnePlayerTwoName}</p> : null}
					</div>

					<div className="flex gap-4 w-full justify-end">
						<Button
							className={`h-10 w-1/2 border border-solid border-gray-300 bg-white text-sm font-semibold leading-5 !text-black hover:bg-gray-100`}
							onClick={onClose}
						>
							Close
						</Button>
						<Button
							onClick={saveScore}
							disabled={isUpdatingMatch}
							className="h-10 w-1/2 bg-orange-500 text-sm font-semibold leading-5 hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed text-white"
						>
							Save
						</Button>
					</div>
					<p
						className="text-center text-orange-500 hover:text-orange-700 cursor-pointer text-sm font-semibold"
						onClick={() => {
							setState((prevState) => ({ ...prevState, isCancelModalOpen: true }));
						}}
					>
						Cancel the match
					</p>
				</div>
			)}
			{state.retireOrForfeitGameInfo ? (
				<ForfeitAndRetireModalContent
					isRetire={state.retireOrForfeitGameInfo.type === 'retire' || Boolean(isMatchInitiallyRetired)}
					isUndo={state.retireOrForfeitGameInfo.type === 'undo'}
					side={state.retireOrForfeitGameInfo.side}
					gameNumber={state.retireOrForfeitGameInfo.gameNumber}
					teamName={state.retireOrForfeitGameInfo.teamName}
					onCancel={() => {
						setState((prevState) => ({ ...prevState, retireOrForfeitGameInfo: null }));
					}}
					onConfirm={onRetireOrForfeitConfirm}
				/>
			) : null}
			{isAlertScoreModalOpen ? (
				<AlertScoreModalContent
					handleSubmit={handleScoreSubmit}
					handleCancel={() => setIsAlertScoreModalOpen(false)}
					textWarningArray={textWarningArray}
				/>
			) : null}
			{state.isCancelModalOpen ? (
				<CancelMatchModalContent
					matchId={match.matchUuid}
					matchupUuid={matchupUuid}
					onCancel={() => {
						setState((prevState) => ({ ...prevState, isCancelModalOpen: false }));
					}}
					onCancelMatchSuccess={() => {
						setState((prevState) => ({ ...prevState, isCancelModalOpen: false }));
						onOpenChange(false);
					}}
				/>
			) : null}
		</ModalWrapper>
	);
};
